import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Printer, ArrowLeft, Copy, Check, Clock, BarChart3 } from "lucide-react";
import { WEEKDAYS, normaliseWeek, fmtTime12 } from "@/lib/businessHours";

// The QR encodes a link into the CONSUMER experience — not the business's
// own dashboard. Configure via REACT_APP_CONSUMER_URL in frontend/.env
// (e.g. `https://app.bookaway.com` or your Expo web preview URL). When
// unset we fall back to the current origin so the QR still resolves — the
// consumer landing (`/`) already handles the `?biz=<id>` deep-link.
const CONSUMER_URL = (
  process.env.REACT_APP_CONSUMER_URL || ""
).replace(/\/+$/, "");

function shareUrl(businessId, headlineIndex) {
  if (!businessId) return "";
  // The QR always points at the smart-link endpoint on our backend, which
  // sniffs the User-Agent and 302's to App Store / Play Store / /download.
  // This means the same printed poster works forever — swapping in real
  // store URLs later is a one-line env change, no reprint needed.
  const base = (process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
  const params = new URLSearchParams({ biz: businessId });
  if (headlineIndex != null) params.set("h", String(headlineIndex));
  return `${base}/api/qr/scan?${params.toString()}`;
}

// Copy variants — printable posters usually feel warmer with a curated line
// than free-form input, so we offer a small set and remember the choice.
const HEADLINES = [
  "Scan to find our last-minute openings!",
  "Book us in seconds — no calls, no wait.",
  "Free slot today? We'll take you now.",
  "Just walked in? Scan for what's open.",
  "Our next available slot — one tap away.",
];

export default function Marketing() {
  const { user } = useAuth();
  const [headlineIdx, setHeadlineIdx] = useState(0);
  const headline = HEADLINES[headlineIdx];
  const [copied, setCopied] = useState(false);
  const canvasWrapRef = useRef(null);
  const [scanCounts, setScanCounts] = useState({}); // {headlineIdx: n}
  const [scanTotal, setScanTotal] = useState(0);

  const url = useMemo(
    () => shareUrl(user?.id, headlineIdx),
    [user?.id, headlineIdx],
  );

  // Load per-headline scan counts so businesses can A/B their poster copy.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/scans/mine");
        if (cancelled) return;
        setScanCounts(data?.counts || {});
        setScanTotal(data?.total || 0);
      } catch {
        // silent — panel simply shows "No scans yet".
      }
    })();
    return () => { cancelled = true; };
  }, []);
  const businessName =
    user?.business_name || user?.name || "Your business";

  const downloadPng = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    if (!canvas) {
      toast.error("QR code not ready yet");
      return;
    }
    // Retina-quality PNG via toDataURL — no external deps.
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `bookaway-qr-${(businessName || "business")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")}.png`;
    link.click();
    toast.success("QR code downloaded");
  };

  const printPoster = () => {
    document.body.classList.add("print-qr-only");
    window.print();
    // Popup blockers / synchronous print return this immediately, safe to clean up.
    document.body.classList.remove("print-qr-only");
  };

  const printHours = () => {
    document.body.classList.add("print-hours-only");
    window.print();
    document.body.classList.remove("print-hours-only");
  };

  const printCombined = () => {
    // Both posters visible, tightened print CSS packs them into one A4.
    document.body.classList.add("print-combined");
    window.print();
    document.body.classList.remove("print-combined");
  };

  const printTent = () => {
    // Landscape A4, only the tent card renders — two mirrored panels split
    // by a fold-line, so it stands on any counter and reads from both sides.
    document.body.classList.add("print-tent-only");
    window.print();
    document.body.classList.remove("print-tent-only");
  };

  const weeklyHours = useMemo(
    () => normaliseWeek(user?.business_hours),
    [user?.business_hours],
  );
  const hasHours = useMemo(
    () => Object.values(weeklyHours).some((d) => !d.closed || d.by_appointment),
    [weeklyHours],
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] p-4 md:p-8">
      {/* Screen-only header */}
      <div className="print:hidden">
        <Link
          to="/dashboard"
          data-testid="marketing-back-link"
          className="mono inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft size={12} /> Dashboard
        </Link>

        <div className="mt-6 border border-border p-6 md:p-8">
          <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
            → marketing kit
          </p>
          <h1 className="mt-2 text-4xl leading-tight tracking-tight md:text-5xl">
            Your poster, print-ready.
          </h1>
          <p className="mt-3 max-w-lg text-sm text-muted-foreground">
            Every business gets a unique QR code. Print it, stick it on your
            front desk, and turn every walk-in into a Book Away follow. Every
            scan lands directly on your live openings.
          </p>
        </div>

        {/* Controls */}
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto] md:items-start">
          <div className="border border-border p-4 md:p-5">
            <p className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground flex items-center justify-between gap-3">
              <span>Poster headline</span>
              <span
                data-testid="scan-total-count"
                className="mono inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#8A7333]"
              >
                <BarChart3 size={11} className="text-[#C9A85F]" />
                {scanTotal} {scanTotal === 1 ? "scan" : "scans"}
              </span>
            </p>
            <div
              data-testid="marketing-headline-picker"
              className="mt-3 flex flex-wrap gap-2"
            >
              {HEADLINES.map((h, idx) => {
                const active = idx === headlineIdx;
                const count = scanCounts[String(idx)] || 0;
                return (
                  <button
                    key={h}
                    data-testid={`headline-choice-${idx}`}
                    onClick={() => setHeadlineIdx(idx)}
                    className={`text-xs px-3 py-2 border transition-colors flex items-center gap-2 ${
                      active
                        ? "border-[#C9A85F] bg-[#C9A85F]/5 text-foreground"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-foreground"
                    }`}
                  >
                    <span>{h}</span>
                    <span
                      data-testid={`headline-scan-count-${idx}`}
                      className={`mono inline-flex min-w-[26px] items-center justify-center px-1.5 py-0.5 text-[9px] uppercase tracking-widest border ${
                        count > 0
                          ? "border-[#C9A85F] bg-[#C9A85F]/15 text-[#8A7333]"
                          : "border-border text-muted-foreground"
                      }`}
                      title={`${count} scan${count === 1 ? "" : "s"} on this headline`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              {CONSUMER_URL ? "Consumer app link" : "Your link"}
            </p>
            <div className="flex items-center gap-2">
              <code
                data-testid="marketing-url"
                className="mono text-[11px] px-2 py-1 bg-muted truncate max-w-[240px] sm:max-w-[420px]"
              >
                {url || "…"}
              </code>
              <button
                data-testid="marketing-copy-link-btn"
                onClick={copyLink}
                aria-label="Copy link"
                className="inline-flex h-9 w-9 items-center justify-center border border-border hover:border-foreground transition-colors"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
              </button>
            </div>
          </div>
          {!CONSUMER_URL && (
            <p
              data-testid="marketing-consumer-url-hint"
              className="mono mt-3 text-[10px] uppercase tracking-widest text-[#C9A85F]/80"
            >
              ⚠ Point QR at your consumer app · set
              REACT_APP_CONSUMER_URL in frontend/.env, then redeploy
            </p>
          )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              data-testid="marketing-download-btn"
              onClick={downloadPng}
              className="inline-flex h-11 items-center gap-2 border border-foreground px-4 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors"
            >
              <Download size={14} /> PNG
            </button>
            <button
              data-testid="marketing-print-btn"
              onClick={printPoster}
              className="inline-flex h-11 flex-1 items-center justify-center gap-2 gold-metallic-bg px-4 text-xs font-semibold uppercase tracking-widest text-black active:scale-[0.98] transition-transform"
            >
              <Printer size={14} /> Print QR
            </button>
            {hasHours && (
              <button
                data-testid="marketing-print-combined-btn"
                onClick={printCombined}
                title="Print QR poster + weekly hours on a single A4 page"
                className="inline-flex h-11 items-center gap-2 border border-[#C9A85F] text-[#8A7333] px-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#C9A85F]/10 active:scale-[0.98] transition-transform"
              >
                <Printer size={14} /> QR + Hours
              </button>
            )}
            <button
              data-testid="marketing-print-tent-btn"
              onClick={printTent}
              title="Print a bi-fold tent card for the reception desk"
              className="inline-flex h-11 items-center gap-2 border border-[#C9A85F] text-[#8A7333] px-4 text-xs font-semibold uppercase tracking-widest hover:bg-[#C9A85F]/10 active:scale-[0.98] transition-transform"
            >
              <Printer size={14} /> Tent card
            </button>
          </div>
        </div>
      </div>

      {/* Poster — this block prints, everything above is `.print:hidden` */}
      <Poster
        canvasWrapRef={canvasWrapRef}
        url={url}
        headline={headline}
        businessName={businessName}
      />

      {/* Hours mini-poster — a second door-ready print card showing the
          weekly schedule. Independent print via `.print-hours-only`. */}
      <HoursPosterCard
        businessName={businessName}
        weeklyHours={weeklyHours}
        hasHours={hasHours}
        onPrint={printHours}
      />

      {/* Tent Card — bi-fold table-top card, landscape A4, dual panels so
          it reads from either side of the counter. Prints via
          `.print-tent-only`. */}
      <TentCard
        businessName={businessName}
        url={url}
        headline={headline}
        onPrint={printTent}
      />

      {/* Social Profile Mark — the official circular avatar for Instagram / TikTok */}
      <SocialMark />

      {/* Print-specific stylesheet */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          body { background: #fff !important; }
          .print\\:hidden { display: none !important; }
          .print-poster,
          .print-hours-poster,
          .print-tent-card {
            box-shadow: none !important;
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
          }
          /* When user hits "Print QR" we hide the hours block and vice-versa
             so each door poster prints on its own single page. */
          body.print-qr-only .print-hours-poster,
          body.print-qr-only .print-tent-card { display: none !important; }
          body.print-hours-only .print-poster,
          body.print-hours-only .print-tent-card { display: none !important; }

          /* Combined mode — both posters on ONE A4. We shrink typography +
             the QR canvas so a top-of-page QR block + bottom-of-page hours
             block fit comfortably without overflowing a second page. */
          body.print-combined .print-poster {
            padding: 0 0 8mm 0 !important;
            border-bottom: 1px dashed #00000030 !important;
            page-break-after: avoid !important;
          }
          body.print-combined .print-hours-poster {
            padding: 8mm 0 0 0 !important;
            page-break-before: avoid !important;
          }
          body.print-combined .print-tent-card { display: none !important; }
          body.print-combined [data-testid="marketing-poster"] .wordmark { font-size: 22pt !important; }
          body.print-combined [data-testid="poster-business-name"] { font-size: 28pt !important; line-height: 1 !important; margin-top: 8pt !important; }
          body.print-combined [data-testid="poster-headline"] { font-size: 12pt !important; margin-top: 8pt !important; }
          body.print-combined [data-testid="marketing-qr"] canvas { width: 200px !important; height: 200px !important; }
          body.print-combined [data-testid="marketing-qr"] { padding: 8px !important; margin-top: 12pt !important; }
          body.print-combined [data-testid="marketing-hours-poster"] { padding: 0 !important; }
          body.print-combined [data-testid="marketing-hours-poster"] .wordmark { font-size: 18pt !important; }
          body.print-combined [data-testid="hours-poster-business-name"] { font-size: 20pt !important; margin-top: 4pt !important; }
          body.print-combined [data-testid="hours-poster-list"] { margin-top: 8pt !important; max-width: 340px !important; }
          body.print-combined [data-testid="hours-poster-list"] li { padding-top: 4pt !important; padding-bottom: 4pt !important; }
          body.print-combined [data-testid^="hours-poster-"] span:last-child { font-size: 12pt !important; }

          /* Tent card — A4 portrait, two mirrored panels stacked so a
             single horizontal crease across the middle folds it into a
             self-standing counter tent. Top panel is rotated 180° so once
             folded backwards, both faces read upright. Full-bleed cream +
             gold accents; browser must honour print colours. */
          body.print-tent-only { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body.print-tent-only .print\\:hidden,
          body.print-tent-only .print-poster,
          body.print-tent-only .print-hours-poster { display: none !important; }
          body.print-tent-only .print-tent-card { display: block !important; }

          /* Pin the tent to the A4 printable box (210mm - 24mm margins =
             186mm × 273mm). Without this the sheet inherits its screen
             pixel width (540px + parent max-w-[1200px] + p-4/p-8 padding)
             and can overflow past the right edge of the page. */
          body.print-tent-only,
          body.print-tent-only .print-tent-card,
          body.print-tent-only [data-testid="tent-sheet"] {
            width: 186mm !important;
            max-width: 186mm !important;
            margin-left: auto !important;
            margin-right: auto !important;
            padding: 0 !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
          }
          /* The tent card's screen ancestor is a max-w-[1200px] container
             with p-4 md:p-8. Kill that outer padding so the printable box
             starts at the @page margin, not 8mm further in. */
          body.print-tent-only > * {
            max-width: 186mm !important;
            padding: 0 !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          /* Trim panel height so 2 × panel + fold guide (~6mm) stays under
             the 273mm printable height with a safety buffer for browser
             DPI variance. Also swap px padding for mm padding so panels
             are DPI-independent on print. */
          body.print-tent-only [data-testid^="tent-panel-"] {
            height: 128mm !important;
            min-height: 128mm !important;
            max-height: 128mm !important;
            padding: 8mm 8mm !important;
            box-sizing: border-box !important;
            overflow: hidden !important;
          }
          body.print-tent-only [data-testid="tent-fold-line"] {
            height: 6mm !important;
            overflow: hidden !important;
          }
        }
      `}</style>
    </div>
  );
}

function SocialMark() {
  return (
    <section
      data-testid="marketing-social-mark"
      className="print:hidden mt-6 border border-border p-6 md:p-8"
    >
      <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
        → social profile mark
      </p>
      <h2 className="mt-2 text-3xl leading-tight tracking-tight md:text-4xl">
        Official Book Away avatar.
      </h2>
      <p className="mt-3 max-w-lg text-sm text-muted-foreground">
        White plate. Thin black frame. Metallic gold "Luxe Pin" — a stylized
        jewel-cut map marker. Use this as your Instagram, TikTok and Threads
        profile picture so followers instantly recognise a Book Away business.
      </p>

      <div className="mt-8 grid gap-6 md:grid-cols-[auto_1fr] md:items-center">
        {/* Preview at profile scale */}
        <div className="flex justify-center">
          <img
            src="/logo-mark.svg"
            alt="Book Away — jewel pin logo mark"
            width="256"
            height="256"
            className="w-40 h-40 md:w-56 md:h-56 rounded-full"
            data-testid="social-mark-preview"
          />
        </div>

        {/* Downloads + platform specs */}
        <div className="grid gap-4">
          <div>
            <p className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              Download for
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href="/logo-mark-1080.png"
                download="bookaway-avatar-1080.png"
                data-testid="social-mark-dl-instagram"
                className="inline-flex h-11 items-center gap-2 border border-foreground px-4 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors"
              >
                <Download size={14} /> Instagram (1080)
              </a>
              <a
                href="/logo-mark-400.png"
                download="bookaway-avatar-400.png"
                data-testid="social-mark-dl-tiktok"
                className="inline-flex h-11 items-center gap-2 border border-foreground px-4 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors"
              >
                <Download size={14} /> TikTok (400)
              </a>
              <a
                href="/logo-mark.svg"
                download="bookaway-avatar.svg"
                data-testid="social-mark-dl-svg"
                className="inline-flex h-11 items-center gap-2 gold-metallic-bg px-4 text-xs font-semibold uppercase tracking-widest text-black active:scale-[0.98] transition-transform"
              >
                <Download size={14} /> SVG (any size)
              </a>
            </div>
          </div>
          <p className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground leading-relaxed">
            → tip: instagram + tiktok crop profile pics into a circle, so the
            frame + plate are already circular-safe. use the svg on print /
            business cards for razor-sharp scaling.
          </p>
        </div>
      </div>
    </section>
  );
}

function Poster({ canvasWrapRef, url, headline, businessName }) {
  return (
    <section
      data-testid="marketing-poster"
      className="print-poster mt-6 border border-border bg-white text-black p-8 md:p-12 flex flex-col items-center text-center"
    >
      {/* Brand wordmark — Playfair Display + metallic gold. Sits above the
          business name so scanners see the source before they see who's
          offering the slot. */}
      <div
        data-testid="poster-wordmark"
        className="wordmark gold-metallic-text text-3xl md:text-4xl"
      >
        Book Away
      </div>
      <p className="mono mt-6 text-[10px] uppercase tracking-[0.35em] text-[#C9A85F]">
        → last-minute openings
      </p>
      <h2
        data-testid="poster-business-name"
        className="mt-4 text-5xl md:text-6xl leading-[0.95] tracking-tight max-w-3xl"
      >
        {businessName}
      </h2>
      <p
        data-testid="poster-headline"
        className="mt-6 text-xl md:text-2xl max-w-2xl leading-snug"
      >
        {headline}
      </p>

      {/* QR — enclosed in a fixed-size wrap so the canvas is easy to grab */}
      <div
        ref={canvasWrapRef}
        data-testid="marketing-qr"
        className="mt-10 border-4 border-black p-4 md:p-6"
      >
        {url ? (
          <QRCodeCanvas
            value={url}
            size={320}
            bgColor="#FFFFFF"
            fgColor="#000000"
            level="H"
            marginSize={2}
            includeMargin={false}
          />
        ) : (
          <div className="w-[320px] h-[320px] flex items-center justify-center text-xs text-muted-foreground skeleton-shimmer">
            Loading…
          </div>
        )}
      </div>

      <p className="mono mt-6 text-[10px] uppercase tracking-widest text-muted-foreground">
        Powered by <span className="wordmark gold-metallic-text text-xs normal-case tracking-normal">Book Away</span> · bookaway.app
      </p>
    </section>
  );
}


// -----------------------------------------------------------------------------
// Hours mini-poster — sits below the QR poster. Screen-only header shows a
// prompt + a dedicated Print button; the .print-hours-poster block is the
// only thing that prints when the user hits it.
// -----------------------------------------------------------------------------
function HoursPosterCard({ businessName, weeklyHours, hasHours, onPrint }) {
  return (
    <section
      data-testid="marketing-hours-card"
      className="print-hours-poster mt-6 border border-border bg-white text-black"
    >
      {/* Screen-only header + print button */}
      <div className="print:hidden flex items-start justify-between gap-4 flex-wrap border-b border-border p-6 md:p-8">
        <div>
          <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
            → hours poster
          </p>
          <h2 className="mt-2 text-3xl leading-tight tracking-tight md:text-4xl">
            Weekly hours, print-ready.
          </h2>
          <p className="mt-3 max-w-lg text-sm text-muted-foreground">
            A crisp door-friendly card showing your weekly schedule. Stick it
            next to your QR poster so passers-by see when to come back.
            {!hasHours && (
              <>
                {" "}
                <Link
                  to="/dashboard/profile"
                  data-testid="hours-poster-set-link"
                  className="underline decoration-[#C9A85F] underline-offset-2 text-foreground"
                >
                  Set your hours →
                </Link>
              </>
            )}
          </p>
        </div>
        {hasHours && (
          <button
            data-testid="hours-poster-print-btn"
            onClick={onPrint}
            className="inline-flex h-11 items-center gap-2 gold-metallic-bg px-4 text-xs font-semibold uppercase tracking-widest text-black active:scale-[0.98] transition-transform"
          >
            <Printer size={14} /> Print hours
          </button>
        )}
      </div>

      {/* The printable card itself */}
      <div
        data-testid="marketing-hours-poster"
        className="p-8 md:p-12 flex flex-col items-center text-center"
      >
        <div className="wordmark gold-metallic-text text-3xl md:text-4xl">
          Book Away
        </div>
        <p className="mono mt-6 text-[10px] uppercase tracking-[0.35em] text-[#C9A85F]">
          → weekly hours
        </p>
        <h3
          data-testid="hours-poster-business-name"
          className="mt-3 text-4xl md:text-5xl leading-[0.95] tracking-tight max-w-3xl"
        >
          {businessName}
        </h3>

        {hasHours ? (
          <ul
            data-testid="hours-poster-list"
            className="mt-10 w-full max-w-md grid gap-0"
          >
            {WEEKDAYS.map(({ key, label }) => {
              const d = weeklyHours[key];
              let value;
              if (d.by_appointment) value = "By appointment";
              else if (d.closed || !d.open || !d.close) value = "Closed";
              else value = `${fmtTime12(d.open)} – ${fmtTime12(d.close)}`;
              const closed = d.closed || (!d.by_appointment && (!d.open || !d.close));
              return (
                <li
                  key={key}
                  data-testid={`hours-poster-${key}`}
                  className="flex items-baseline justify-between border-b border-black/15 py-3"
                >
                  <span className="mono text-sm uppercase tracking-[0.28em] text-black">
                    {label}
                  </span>
                  <span
                    className={`text-lg tracking-tight ${
                      closed ? "text-black/50" : "text-black"
                    }`}
                  >
                    {value}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p
            data-testid="hours-poster-empty"
            className="mono mt-10 text-xs uppercase tracking-widest text-black/50"
          >
            No hours set yet — add them from your profile.
          </p>
        )}

        <div className="mt-10 h-px w-24 bg-black/30" aria-hidden="true" />
        <p className="mono mt-6 text-[10px] uppercase tracking-widest text-black/60">
          Same-day slots go live on <span className="wordmark gold-metallic-text text-xs normal-case tracking-normal">Book Away</span> · bookaway.app
        </p>
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// Tent Card — a bi-fold counter card that stands on the reception desk with
// the SAME face on both sides. We render an A4-portrait sheet split into two
// panels of equal height; the top panel is rotated 180° so once the user folds
// the sheet backwards along the centre crease, both faces read upright.
//
// Brand rails (never override):
//   • Marble Cream #F7F5F2 — panel background
//   • Champagne Gold #C9A85F — accents, fold-line, QR frame
//   • Deep Charcoal #2A2724 — text
// -----------------------------------------------------------------------------
function TentCard({ businessName, url, headline, onPrint }) {
  // A single panel is reused twice with a rotation, so any tweak lands in one
  // place — critical to keeping the two faces pixel-identical.
  const Panel = ({ inverted }) => (
    <div
      data-testid={`tent-panel-${inverted ? "back" : "front"}`}
      className="relative flex flex-col items-center justify-between text-center px-8 py-10"
      style={{
        background: "#F7F5F2",
        color: "#2A2724",
        // Portrait A4 = 297mm tall. Two panels of 138mm each + ~20mm reserved
        // for margins/fold guides keeps the layout inside the printable area.
        minHeight: "138mm",
        transform: inverted ? "rotate(180deg)" : "none",
      }}
    >
      {/* Corner ticks — subtle gold registration marks add print-shop polish */}
      <span
        aria-hidden="true"
        className="absolute top-3 left-3 w-4 h-4"
        style={{ borderTop: "1px solid #C9A85F", borderLeft: "1px solid #C9A85F" }}
      />
      <span
        aria-hidden="true"
        className="absolute top-3 right-3 w-4 h-4"
        style={{ borderTop: "1px solid #C9A85F", borderRight: "1px solid #C9A85F" }}
      />
      <span
        aria-hidden="true"
        className="absolute bottom-3 left-3 w-4 h-4"
        style={{ borderBottom: "1px solid #C9A85F", borderLeft: "1px solid #C9A85F" }}
      />
      <span
        aria-hidden="true"
        className="absolute bottom-3 right-3 w-4 h-4"
        style={{ borderBottom: "1px solid #C9A85F", borderRight: "1px solid #C9A85F" }}
      />

      {/* Top block — wordmark + eyebrow + business name */}
      <div className="flex flex-col items-center">
        <div className="wordmark gold-metallic-text text-3xl">Book Away</div>
        <p
          className="mono mt-3 text-[9px] uppercase"
          style={{ letterSpacing: "0.35em", color: "#C9A85F" }}
        >
          → last-minute openings
        </p>
        <h3
          data-testid="tent-business-name"
          className="mt-4 text-3xl leading-[0.98] tracking-tight max-w-xs"
        >
          {businessName}
        </h3>
      </div>

      {/* Middle block — QR framed in gold, with headline directly above */}
      <div className="flex flex-col items-center">
        <p
          data-testid="tent-headline"
          className="text-base leading-snug max-w-[240px] mb-4"
        >
          {headline}
        </p>
        <div
          data-testid="tent-qr"
          className="p-3"
          style={{
            background: "#FFFFFF",
            border: "3px solid #C9A85F",
            boxShadow: "0 0 0 6px #F7F5F2, 0 0 0 7px #C9A85F",
          }}
        >
          {url ? (
            <QRCodeCanvas
              value={url}
              size={180}
              bgColor="#FFFFFF"
              fgColor="#2A2724"
              level="H"
              marginSize={2}
              includeMargin={false}
            />
          ) : (
            <div className="w-[180px] h-[180px] skeleton-shimmer" />
          )}
        </div>
        <p
          className="mono mt-4 text-[9px] uppercase"
          style={{ letterSpacing: "0.28em", color: "#2A2724" }}
        >
          scan · book · done
        </p>
      </div>

      {/* Footer — anchor line + attribution */}
      <div className="flex flex-col items-center">
        <div className="h-px w-16" style={{ background: "#C9A85F" }} aria-hidden="true" />
        <p className="mono mt-3 text-[9px] uppercase tracking-widest" style={{ color: "#2A2724" }}>
          bookaway.app
        </p>
      </div>
    </div>
  );

  return (
    <section
      data-testid="marketing-tent-card"
      className="print-tent-card mt-6 border border-border bg-white text-black"
    >
      {/* Screen-only header + print button */}
      <div className="print:hidden flex items-start justify-between gap-4 flex-wrap border-b border-border p-6 md:p-8">
        <div>
          <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
            → counter tent card
          </p>
          <h2 className="mt-2 text-3xl leading-tight tracking-tight md:text-4xl">
            Two-sided tent, one fold.
          </h2>
          <p className="mt-3 max-w-lg text-sm text-muted-foreground">
            Prints on a single A4 page in your exact brand colours — Marble
            Cream, Champagne Gold, Deep Charcoal. Fold along the dashed
            centre line so both sides read upright; sits on your reception
            desk and greets every guest with your QR.
          </p>
          <p className="mono mt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
            Tip · print on 200gsm+ card stock for a proper tent feel.
          </p>
        </div>
        <button
          data-testid="tent-print-btn"
          onClick={onPrint}
          className="inline-flex h-11 items-center gap-2 gold-metallic-bg px-4 text-xs font-semibold uppercase tracking-widest text-black active:scale-[0.98] transition-transform"
        >
          <Printer size={14} /> Print tent card
        </button>
      </div>

      {/* Sheet preview — this is what prints when .print-tent-only is set */}
      <div
        data-testid="tent-sheet"
        className="mx-auto my-6 md:my-10 w-full max-w-[540px]"
        style={{ background: "#F7F5F2" }}
      >
        {/* Back panel (rotated 180°) — reads upright once folded backwards */}
        <Panel inverted={true} />

        {/* Fold guide */}
        <div
          data-testid="tent-fold-line"
          className="relative h-6 flex items-center justify-center"
          style={{ background: "#F7F5F2" }}
        >
          <div
            className="w-full"
            style={{
              borderTop: "1px dashed #C9A85F",
              opacity: 0.9,
            }}
          />
          <span
            className="mono absolute px-3 text-[9px] uppercase tracking-[0.32em]"
            style={{ background: "#F7F5F2", color: "#C9A85F" }}
          >
            fold here
          </span>
        </div>

        {/* Front panel — the face customers see first */}
        <Panel inverted={false} />
      </div>
    </section>
  );
}
